import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Notify } from "../notifySlice";
const { REACT_APP_API_ENDPOINT: baseUrl } = process.env;

export type User = {
    avatar?: string;
    user_id: number;
    user_name: string;
    user_email: string;
    registration_date: string;

    ref_points: number;
    ref_token: string;
    ref_url: string;
    spend_ref_points: number;
    referrals_count: number;

    chat_config: string;
    chat_token: string;
    no_mailing: boolean;
    telegram_id?: number;
    studio_support: boolean;
    admin?: boolean;
    tariff: {
        id: number;
        name: keyof typeof UserTariff;
        days_left: number;
        start_at: string;
        end_at: string;
        max_online_outputs: number;
        price: number;
        max_input_bandwidth: number;
        max_active_transcodes: number;
        studio: boolean;
        max_studio_resolution: string;
        custom_links: boolean;
    };

    is_first_login: boolean;
};

export interface TelegramConnectResponse {
    url: string;
}

export interface SignUpParams {
    email: string;
    password: string;
    passwordConfirmation: string;
    utmTag: string | null;
    referralToken: string | null;
}

// * Tariff name with channel count for this tariff
export enum UserTariff {
    "R2" = 2,
    "R5" = 5,
    "R10" = 10,
}

export type Broadcast = {
    id: number;
    name: string;
    description?: string;
    start_at: string;
    end_at: string;
    rtmp_key: string;
    capture_url: string;
    iframe_code: string;
    iframe_url: string;
    m3u8_url: string;
    status: 'offline' | 'starting' | 'stopping' | 'online';
    restream_templates: Channel[];
    error: string;
    current_bitrate: number;
    bitrates: number[];
    server: string;
};

export type PlannedBroadcast = {
    id: Broadcast['id'];
    outer_title: Channel['outer_title'];
    outer_desc: Channel['outer_desc'];
    start_time: Broadcast['start_at'];
    latency: Channel['latency'];
    category_id: Channel['category_id'];
    category_name: Channel['category_name'];
}

export type BroadcastList = Broadcast[];

export type BroadcastsData = {
    limit: number;
    offset: number;
    total: number;
    broadcasts: BroadcastList;
};

export type Success = {
    success: boolean;
};

// Planned streams
export interface IPlannedStream {
    restreamId: string;
    title: string;
    type: "channel" | "event";
    scheduled_start_time?: string;
    description: string;
    platform: Platform;
}

export interface IYoutubeStream extends IPlannedStream {
    id: string;
    privacy_status: "public" | "private";
    live_chat_id?: string;
    thumbnails: Record<"default" | "high" | "medium", { url: string; height: number; width: number }>;
}

export interface IVkEventInfo extends IPlannedStream {
    type: "event";
    owner_id: number;
    thumbnails: {
        url: "https://vk.ru/images/video/thumbs/video_x.png";
        width: 800;
        height: 450;
        with_padding: 1;
    };
    height: 450;
    url: string;
    width: number;
    with_padding: number;
    title: string;
    video_id: number;
}

export interface IVkStreamInfo extends IPlannedStream {
    thumbnails: {
        url: string;
    };
    title: string;
    id: number;
}

export interface IVkStream extends IPlannedStream {
    account: IVkStreamInfo;
    groups: IVkStreamInfo[];
}

export interface VkSelectedItemPlannedBroadcast {
    type: "channel" | "event";
    owner_id: number;
    title: string;
    video_id?: number;
    scheduled_start_time?: string;
    description: string;
}

export interface CreateRutubeQueryBody {
  channel_link: string;
  target_url: string;
  key: string;
};

export type Platform =
    'restream' |
    'youtube' |
    'rutube' |
    'twitch' |
    'trovo' |
    'nuum' |
    'platform' |
    'vk_video_live' |
    'gg' |
    'vk' |
    'ok' |
    'telegram' |
    'kick' |
    'goodgame' |
    'boosty' |
    'vk_play_live';

// Channel aka restream template on the server
export enum ChannelError {
    "TOKEN_AUTH_ERROR" = "TOKEN_AUTH_ERROR",
    "KEY_REQUIRED" = "KEY_REQUIRED",
    "STREAM_ERROR" = "STREAM_ERROR",
    "STREAM_BITRATE_WARNING" = "STREAM_BITRATE_WARNING",
    "OUTDATED_CHANNEL_ERROR" = "OUTDATED_CHANNEL_ERROR",
    "UNSUPPORTED_PLATFORM" = "UNSUPPORTED_PLATFORM"
}

export type Channel = {
    id: number;
    name: string;
    target_url: string;
    key: string;
    enabled: boolean;
    api_authorized?: boolean;
    platform?: Platform;
    category_id?: ChannelCategory['id'];
    category_name?: ChannelCategory['name'];
    subcategory_id?: ChannelCategory['id'];
    subcategory_name?: ChannelCategory['name'];
    transcode_preset_id: TranscodePreset["id"];
    outer_title: string;
    outer_desc: string;
    vk_wallpost?: boolean;
    error?: ChannelError;
    latency?: ChannelLatencyType;
    status: "online" | "offline" | "disabled";
    transcode_preset?: TranscodePreset;
    telegram_chat: TelegramChat;
    uptime: number;
    next_restart_time?: string;
    channel_link: string;
};

export type ChannelLinkTelegram = {
    bot_command: string;
    invite_url: string;
    token?: string;
}

export type TelegramChat = {
    id: number;
    title: string;
    posts: TelegramPost[];
    last_post: string;
    chat_mode: string;
}

export type TelegramPost = {
    channel_title: string;
    post_id: string;
    active: boolean;
    post_link: string;
    title: string;
}

export type ChannelStatusType = "ready" | "active" | "inactive";
export type ChannelLatencyType = "normal" | "low" | "ultra_low";

export interface ChannelCategory {
    id: string;
    name: string;
    box_art_url?: string;
    subcategories?: ChannelCategory[];
}

export type ChannelData = {
    limit: number;
    offset: number;
    total: number;
    restream_templates: Channel[];
};

export type TariffsData = {
    limit: number;
    offset: number;
    total: number;
    tariffs: Tariff[];
};

export interface Tariff {
    id: number;
    name: string;
    description: string;
    max_online_inputs: number;
    max_online_outputs: number;
    promo: boolean; // Акционное предложение или нет
    promo_price: number; // Цена по акции
    price: number;
    duration: number;
    features: string[];
    max_active_transcodes: number;
    studio: boolean;
}

interface IResponseWithPagination {
    limit: number;
    offset: number;
    total: number;
}

export interface RequestPaginationParams {
    page: number;
    per_page: number;
}

export type ChannelTemplate = {
    title: string;
    desc: string;
}

export interface Transaction {
    id: number;
    tariff_id: number;
    tariff_name: string;
    sum: number; // * Сумма платежа
    buy_date: string;
    terminate_date: string | null;
    status: "aborted" | "completed" | "started";
}

export interface KickLink {
    auth_link: string;
}

export interface VkVideoLiveLink {
    auth_link: string;
}

export interface TransactionsData extends IResponseWithPagination {
    transactions: Transaction[];
}

export interface NotificationsData extends IResponseWithPagination {
    notifications: Notify[];
    any_unread: boolean;
}

export interface MarkNotificationsAsReadOptions {
    personalNotificationIds?: (number | undefined)[];
    systemNotificationIds?: (number | undefined)[];
}
export interface Message {
    platform?: Platform;
    user: string;
    message?: string;
    date?: number; // * Technical message has no date
    error?: {
        captcha_sid?: number;
        captcha_img?: string;
    };
    target_platform?: string[];
}

export type WidgetSettings = {
    name_color?: string;
    message_color?: string;
    background_color?: string;
    id?: string;
    chat_token?: string;
    scale: string;
}

export type TranscodePreset = {
    id: number;
    name: string;
    codec: string;
    video_bitrate: number;
    audio_bitrate: number;
    fps: number;
    gop: number;
    height: number;
    width: number;
    user_id: number;
    created_at: string;
    updated_at: string;
}
export interface UserStudio {
    id: number;
    token: string;
    user_id: number;
    broadcast: Broadcast;
    status: string;
    error: string;
    max_scenes_count: number;
    max_templates_count: number;
    current_scene: Scene;
    studio_scenes: Scene[];
    studio_templates: StudioTemplate[];
    backgrounds: Background[];
    orientation: "landscape" | "portrait";
    logos: Logo[];
    resolution: string;
}

export interface StudioPage {
    studio_token: string;
    studio_id: UserStudio["id"];
}

export interface Scene {
  id: number;
  title: string;
  background: Background;
  logo: Logo;
  current_template: StudioTemplate;
}

export interface Background {
    id: string;
    type: string;
    url: string;
}

export interface Logo {
    id: string;
    url: string;
}

export interface TemplateElement {
    type: 'camera' | 'screen';
    top: string;
    left: string;
    width: string;
    height: string;
    z_index: string;
    display: string;
}

export interface StudioTemplate {
    title: string;
    id: number;
    elements: TemplateElement[];
}
export interface IStudio {
    token: string;
}

export interface refreshChannelResponse {
    next_restart_time: string;
}

export const BROADCASTS_TAG = 'broadcasts';
export const CHANNELS_TAG = 'channels';
export const TRANSCODE_PRESET_TAG = 'transcodePreset';
export const USER_TAG = 'user';
const NOTIFY_TAG = 'notify';
export const STUDIO = 'studio';

export const apiSlice = createApi({
    baseQuery: fetchBaseQuery({
        baseUrl,
        credentials: 'include',
        prepareHeaders(headers) {
            // ? does not allow sending FormData with an image to the server
            // headers.set('Content-Type', 'application/json');
            headers.set('accept', 'application/json');
            return headers;
        }
    }),
    tagTypes: [BROADCASTS_TAG, CHANNELS_TAG, USER_TAG, NOTIFY_TAG, TRANSCODE_PRESET_TAG, STUDIO],
    endpoints(builder) {
        return {
            // Broadcasts
            getBroadcasts: builder.query<BroadcastsData, void>({
                query: () => '/api/broadcasts',
                providesTags: [BROADCASTS_TAG]
            }),
            // Broadcast
            getBroadcast: builder.query<Broadcast, Broadcast['id']>({
                query: (id) => `/api/broadcasts/${id}`,
                providesTags: [BROADCASTS_TAG],
                transformResponse: (broadcast: {broadcast: Broadcast}) => ({ ...broadcast.broadcast })
            }),
            createBroadcast: builder.mutation<{ broadcast: Broadcast }, Pick<Broadcast, 'name' | 'start_at' | 'description'>>({
                query: (body) => ({
                    url: '/api/broadcasts',
                    method: 'POST',
                    body
                }),
                invalidatesTags: [BROADCASTS_TAG]
            }),
            deleteBroadcast: builder.mutation<Success, Broadcast['id']>({
                query: (id) => ({
                    url: `/api/broadcasts/${id}`,
                    method: 'DELETE'
                }),
                invalidatesTags: [BROADCASTS_TAG]
            }),
            updateBroadcast: builder.mutation<Broadcast, { id: Broadcast['id']; body: Pick<Broadcast, 'name' | 'start_at' | 'rtmp_key' | 'description'> }>({
                query: ({ id, body }) => ({
                    url: `/api/broadcasts/${id}`,
                    method: 'PUT',
                    body
                }),
                invalidatesTags: [BROADCASTS_TAG, STUDIO]
            }),
            startBroadcast: builder.mutation<Success, Broadcast['id']>({
                query: (id) => ({
                    url: `/api/broadcasts/${id}/start`,
                    method: 'POST'
                }),
                invalidatesTags: [BROADCASTS_TAG]
            }),
            stopBroadcast: builder.mutation<Success, Broadcast['id']>({
                query: (id) => ({
                    url: `/api/broadcasts/${id}/stop`,
                    method: 'POST'
                }),
                invalidatesTags: [BROADCASTS_TAG]
            }),
            changeRtmpKey: builder.mutation<Broadcast, { broadcastId: Broadcast['id'] }>({
                query: ({ broadcastId }) => ({
                    url: `/api/broadcasts/${broadcastId}/rtmp_key_generate`,
                    method: 'POST'
                }),
                invalidatesTags: [BROADCASTS_TAG]
            }),
            plannedBroadcast: builder.mutation<Success, PlannedBroadcast>({
                query: ({ id, outer_title, outer_desc, start_time, latency, category_id, category_name }) => ({
                    url: `/api/broadcasts/${id}/youtube_upcoming`,
                    method: 'POST',
                    body: {
                        outer_title,
                        outer_desc,
                        start_time,
                        latency,
                        category_id,
                        category_name
                    }
                }),
                invalidatesTags: [BROADCASTS_TAG]
            }),
            getVkPlannedBroadcast: builder.query<IVkEventInfo[], {owner_id: number}>({
                query: ({ owner_id }) => ({
                    url: `/api/restream_templates/owner_events`,
                    method: "GET",
                    params: {
                        owner_id
                    }
                }),
                transformResponse: (response: {events: IVkEventInfo[]}) => response.events,
            }),
            // preset
            getTranscodePresets: builder.query<{ transcode_presets: TranscodePreset[] } & IResponseWithPagination, RequestPaginationParams>({
                query: ({ page, per_page }) => ({
                    url: `/api/transcode_presets`,
                    method: "GET",
                    params: {
                        page,
                        per_page
                    }
                }),
                providesTags: [TRANSCODE_PRESET_TAG, BROADCASTS_TAG, CHANNELS_TAG]
            }),
            createTranscodePreset: builder.mutation<void, Partial<TranscodePreset>>({
                query: (body) => ({
                    url: `/api/transcode_presets`,
                    method: "POST",
                    body
                }),
                invalidatesTags: [TRANSCODE_PRESET_TAG, BROADCASTS_TAG, CHANNELS_TAG]
            }),
            updateTranscodePreset: builder.mutation<void, Partial<TranscodePreset>>({
                query: ({id, ...body}) => ({
                    url: `/api/transcode_presets/${id}`,
                    method: "PUT",
                    body: {
                        id,
                        ...body
                    }
                }),
                invalidatesTags: [TRANSCODE_PRESET_TAG, BROADCASTS_TAG, CHANNELS_TAG]
            }),
            deleteTranscodePreset: builder.mutation<void, {id: number}>({
                query: ({ id }) => ({
                    url: `/api/transcode_presets/${id}`,
                    method: "DELETE",
                }),
                invalidatesTags: [TRANSCODE_PRESET_TAG, BROADCASTS_TAG, CHANNELS_TAG]
            }),

            // User
            getUser: builder.query<User, string | void>({
                query: (token) => token ? `/api/user?chat_token=${token}` : "/api/user",
                providesTags: [USER_TAG]
            }),
            uploadAvatar: builder.mutation<User, { body: FormData; uid?: User["user_id"] }>({
                query: ({ body, uid }) => ({
                    url: `/api/users/${uid}/update_avatar`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: [USER_TAG],
            }),
            updateRefToken: builder.mutation<void, { uid: number; ref_token: string }>({
                query: ({ uid, ref_token }) => ({
                    url: `/api/users/${uid}/update_ref_token`,
                    method: "POST",
                    body: {
                        ref_token
                    }
                }),
                invalidatesTags: [USER_TAG],
            }),

            // * Notifications
            getNotifications: builder.query<NotificationsData, { isUnread: boolean; page?: number; limit?: number }>({
                query: ({ isUnread, page = 1, limit = 20 }) => ({
                    url: `/api/notifications?unread=${Number(isUnread)}&page=${page}&per_page=${limit}`
                }),
                providesTags: [NOTIFY_TAG]
            }),

            markNotificationAsRead: builder.mutation<void, MarkNotificationsAsReadOptions>({
                query: ({ personalNotificationIds, systemNotificationIds }) => ({
                    method: "POST",
                    url: "/api/notifications/mark_as_read",
                    body: {
                        personal_ids: personalNotificationIds,
                        system_ids: systemNotificationIds,
                    }
                }),
                invalidatesTags: [NOTIFY_TAG]
            }),

            // Channels
            getChannels: builder.query<ChannelData, void>({
                query: () => '/api/restream_templates',
                providesTags: [CHANNELS_TAG, STUDIO]
            }),

            connectTelegram: builder.query<TelegramConnectResponse, void>({
                query: () => ({
                    url: `/api_auth/telegram_auth_token`,
                    method: 'GET',
                }),
            }),

            fetchRestreamTemplatesWithBroadcast: builder.query<ChannelData, { broadcastId: Broadcast['id']; group: string } & Partial<RequestPaginationParams>>({
                query: ({ broadcastId, group, page, per_page = 12 }) => ({
                    url: `/api/broadcasts/${broadcastId}/restream_templates`,
                    method: 'GET',
                    params: { group, per_page, page },
                }),
                providesTags: [CHANNELS_TAG, BROADCASTS_TAG, STUDIO]
            }),

            selectYoutubeStreams: builder.mutation<void, { streams: IYoutubeStream[]; broadcastId?: Broadcast["id"] }>({
                query: ({ streams, broadcastId }) => ({
                    url: `/api/broadcasts/${broadcastId}/youtube_select_stream`,
                    method: "POST",
                    body: {
                        streams
                    }
                }),
                invalidatesTags: [BROADCASTS_TAG]
            }),

            selectVkStreams: builder.mutation<void, { streams: VkSelectedItemPlannedBroadcast[]; broadcastId?: Broadcast["id"] }>({
                query: ({ streams, broadcastId }) => ({
                    url: `/api/broadcasts/${broadcastId}/vk_select_stream`,
                    method: "POST",
                    body: {
                        streams
                    }
                }),
                invalidatesTags: [BROADCASTS_TAG]
            }),

            createTelegramChatLink:builder.mutation<ChannelLinkTelegram, { channelId?: Channel["id"] }>({
                query: ({ channelId }) => ({
                    url: `/api/restream_templates/${channelId}/telegram_chat`,
                    method: "POST",
                }),
                invalidatesTags: [CHANNELS_TAG]
            }),

            updateTelegramChatMode: builder.mutation<TelegramChat, { id: number; chat_mode: string }>({
                query: ({ id, chat_mode }) => ({
                    url: `/api/restream_templates/${id}/telegram_chat`,
                    method: "PUT",
                    body: { chat_mode }
                }),
                invalidatesTags: [CHANNELS_TAG]
            }),

            deleteTelegramConnect: builder.mutation<Success, Channel['id']>({
                query: (id) => ({
                    url: `/api/restream_templates/${id}/telegram_chat`,
                    method: 'DELETE'
                }),
                invalidatesTags: [CHANNELS_TAG]
            }),

            refreshChannel: builder.mutation<refreshChannelResponse, { broadcastId: Broadcast["id"]; channelId: Channel["id"] }>({
                query: ({ broadcastId, channelId }) => ({
                    url: `/api/broadcasts/${broadcastId}/restart_template/${channelId}`,
                    method: "POST"
                }),
                invalidatesTags: [BROADCASTS_TAG]
            }),

            // * Chat widget
            widgetSettings: builder.mutation<{ chat_config: WidgetSettings }, { body: WidgetSettings; uid?: User["user_id"] }>({
                query: ({ body, uid }) => ({
                    url: `/api/users/${uid}/chat_config`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: [USER_TAG],
            }),

            // * Multichat
            getChatMessages: builder.query<{ response: Message[] }, { id?: string; token?: string }>({
                query: ({ id, token }) => `/api/broadcasts/${id}/multichat?chat_token=${token}`,
            }),

            // Channel
            createChannel: builder.mutation<{ restream_template: Channel }, Pick<Channel, 'name' | 'target_url' | 'key' | 'platform'>>({
                query: (params) => ({
                    url: '/api/restream_templates',
                    method: 'POST',
                    body: params
                }),
                invalidatesTags: [BROADCASTS_TAG, CHANNELS_TAG, STUDIO]
            }),
            createRutubeChannel: builder.mutation<void, CreateRutubeQueryBody>({
                query: ({ ...rutubeParams }) => ({
                    url: '/api/rutube_callback',
                    method: "POST",
                    body: { ...rutubeParams }
                }),
                invalidatesTags: [BROADCASTS_TAG, CHANNELS_TAG, STUDIO]
            }),
            getChannelCategories: builder.query<ChannelCategory[], { channelId?: Channel['id']; categoryName?: string }>({
                query: ({ channelId, categoryName }) => ({
                    url: `/api/restream_templates/${channelId}/categories`,
                    method: 'GET',
                    params: {
                        search_name: categoryName
                    }
                }),

            }),
            updateChannel: builder.mutation<Pick<ChannelData, 'restream_templates'>, Partial<Channel>>({
                query: ({ id, ...restParams }) => ({
                    url: `/api/restream_templates/${id}`,
                    method: 'PUT',
                    body: restParams
                }),
                invalidatesTags: [BROADCASTS_TAG, CHANNELS_TAG, STUDIO]
            }),
            deleteChannel: builder.mutation<Success, Channel['id']>({
                query: (id) => ({
                    url: `/api/restream_templates/${id}`,
                    method: 'DELETE'
                }),
                invalidatesTags: [BROADCASTS_TAG, CHANNELS_TAG, STUDIO]
            }),
            deleteChannels: builder.mutation<Success, number[]>({
                query: (ids) => {
                    const params = new URLSearchParams();
                    ids.forEach(id => params.append('ids[]', id.toString()));
                    return {
                        url: `/api/restream_templates?${params.toString()}`,
                        method: 'DELETE'
                    };
                },
                invalidatesTags: [BROADCASTS_TAG, CHANNELS_TAG, STUDIO]
            }),
            getDefaultTemplates: builder.query<ChannelTemplate, {chanelId: Channel['id']}>({
                query: ({chanelId}) => ({
                    url: `/api/restream_templates/${chanelId}/default_templates`,
                    method: 'GET'
                }),
            }),
            enableChannel: builder.mutation<Success, { broadcastId: Broadcast['id']; channelId: Channel['id'] }>({
                query: ({ broadcastId, channelId }) => ({
                    url: `/api/broadcasts/${broadcastId}/template_on/${channelId}`,
                    method: 'POST'
                }),
                invalidatesTags: [BROADCASTS_TAG, CHANNELS_TAG, STUDIO]
            }),
            disableChannel: builder.mutation<Success, { broadcastId: Broadcast['id']; channelId: Channel['id'] }>({
                query: ({ broadcastId, channelId }) => ({
                    url: `/api/broadcasts/${broadcastId}/template_off/${channelId}`,
                    method: 'POST'
                }),
                invalidatesTags: [BROADCASTS_TAG, CHANNELS_TAG, STUDIO]
            }),
            toggleChannels: builder.mutation<Success, { broadcastId: Broadcast['id']; target: string;  group: string; page: number }>({
                query: ({ broadcastId, target, group, page }) => ({
                    url: `/api/broadcasts/${broadcastId}/toggle_templates`,
                    method: "POST",
                    body: {
                        target,
                        group,
                        page,
                        per_page: 12
                    },
                }),
                invalidatesTags: [BROADCASTS_TAG, CHANNELS_TAG, STUDIO]
            }),
            getChannelStatus: builder.query<ChannelStatusType, { channelId?: Channel['id'] }>({
                query: ({ channelId }) => `/api/restream_templates/${channelId}/check_status`,
                transformResponse(response: { status: ChannelStatusType }) {
                    return response.status;
                },
            }),
            getChannelKickLink: builder.query<KickLink, void>({
                query: () => ({
                    url: `/api/kick_auth_link`,
                      method: 'GET'
                }),
            }),
            getChannelVkVideoLiveLink: builder.query<VkVideoLiveLink, void>({
                query: () => ({
                    url: `/api/vk_live_auth_link`,
                    method: 'GET'
                }),
            }),
            // Tariffs
            getTariffs: builder.query<TariffsData, void>({
                query: () => '/api/tariffs'
            }),
            buyTariff: builder.mutation<{ buy_url: string }, { id: TariffsData['tariffs'][0]['id']; refPoints?: number }>({
                query: ({ id, refPoints = 0 }) => ({
                    url: `/api/buy`,
                    method: 'POST',
                    body: { tariff_id: id, spend_ref_points: refPoints }
                })
            }),
            getTransactions: builder.query<TransactionsData, { limit: number; page: number }>({
                query: ({ limit, page }) => `/api/transactions?page=${page}&per_page=${limit}`
            }),
            // Auth
            signIn: builder.mutation<{ message: string }, { email: string; password: string }>({
                query: ({ email, password }) => ({
                    url: '/api_auth/sign_in',
                    method: 'POST',
                    body: {
                        user: {
                            email,
                            password
                        }
                    }
                }),
            }),
            register: builder.mutation<{ message: string }, SignUpParams>({
                query: ({ email, password, passwordConfirmation, utmTag, referralToken }) => ({
                    url: '/api_auth/sign_up',
                    method: 'POST',
                    body: {
                        user: {
                            email,
                            password,
                            password_confirmation: passwordConfirmation,
                        },
                        ref_token: referralToken,
                        utm_tag: utmTag,
                    }
                })
            }),
            logout: builder.mutation<{ message: string }, void>({
                query: () => ({
                    url: '/api_auth/sign_out',
                    method: 'DELETE'
                }),
                invalidatesTags: [USER_TAG]
            }),
            passwordRecovery: builder.mutation<{ message: string }, string>({
                query: (email) => ({
                    url: '/api_auth/password',
                    method: 'POST',
                    body: {
                        user: {
                            email
                        }
                    }
                }),
            }),
            passwordReset: builder.mutation<{ message: string }, { token: string; password: string; confirmation: string }>({
                query: ({ token, password, confirmation }) => ({
                    url: '/api_auth/password',
                    method: 'PUT',
                    body: {
                        user: {
                            reset_password_token: token,
                            password: password,
                            password_confirmation: confirmation
                        }
                    }
                })
            }),
            // * Utm
            updateUtm: builder.mutation<void, { userId: number; utmTag: string }>({
                query: ({ userId, utmTag }) => ({
                    method: "POST",
                    url: `/api/users/${userId}/update_utm_tag`,
                    body: {
                        utm_tag: utmTag,
                    }
                })
            }),
            // Profile
            changePassword: builder.mutation<{ message: string }, { currentPassword: string; password: string; passwordConfirmation: string }>({
                query: ({ currentPassword, password, passwordConfirmation }) => ({
                    url: '/api_auth/change_password',
                    method: 'PUT',
                    body: {
                        user: {
                            password,
                            password_confirmation: passwordConfirmation,
                            current_password: currentPassword
                        }
                    }
                }),
            }),
            changeEmail: builder.mutation<{ message: string }, { email: string; password: string; userId?: number }>({
                query: ({ email, password, userId }) => ({
                    url: `/api/users/${userId}/account_with_password`,
                    method: 'PUT',
                    body: {
                        user: {
                            email,
                            password
                        }
                    }
                }),
                // invalidatesTags: [USER_TAG]
            }),
            toggleMailing: builder.mutation<void, { userId?: number; noMailing: boolean }>({
                query: ({ userId, noMailing }) => ({
                    url: `/api/users/${userId}/account`,
                    method: 'PUT',
                    body: {
                        user: {
                            no_mailing: noMailing,
                        }
                    }
                }),
            }),
            // Studio
            getStudio: builder.query<UserStudio, void >({
                query: () => ({
                    method: "GET",
                    url: `/api/studio`,
                }),
                providesTags: [STUDIO],
                transformResponse: (studio: { studio: UserStudio }) => ({ ...studio.studio })
            }),

            createScene: builder.mutation<Scene, void>({
                query: () => ({
                    url: `/api/studio/scenes`,
                    method: 'POST'
                }),
                invalidatesTags: [STUDIO]
            }),

            updateScene: builder.mutation<Scene, { id: Scene["id"]; title: Scene["title"] }>({
                query: ({ id: scene_id, title }) => ({
                    url: `/api/studio/scenes/${scene_id}`,
                    method: 'PUT',
                    body: {
                        title
                    }
                }),
                invalidatesTags: [STUDIO]
            }),

            deleteScene: builder.mutation<Scene, { id: Scene["id"] }>({
                query: ({ id: scene_id }) => ({
                    url: `/api/studio/scenes/${scene_id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: [STUDIO]
            }),

            startStudioBroadcast: builder.mutation<void, void>({
                query: () => ({
                    url: `/api/studio/start/`,
                    method: 'POST'
                }),
                invalidatesTags: [STUDIO]
            }),

            stopStudioBroadcast: builder.mutation<void, void>({
                query: () => ({
                    url: `/api/studio/stop/`,
                    method: 'POST'
                }),
                invalidatesTags: [STUDIO]
            }),

            changeTemplateStudio: builder.mutation<void, {templateId: number}>({
                query: ({templateId}) => ({
                    url: `/api/studio/scenes/change_template/${templateId}`,
                    method: 'POST',
                }),
                invalidatesTags: [STUDIO]
            }),
            changeBackgroundStudio: builder.mutation<void, {id: string; background: Background}>({
                query: ({id, background}) => ({
                    url: `/api/studio/scenes/change_background/${id}`,
                    method: 'POST',
                    body: background
                }),
                invalidatesTags: [STUDIO]
            }),
            changeSceneStudio: builder.mutation<void, { sceneId: number }>({
                query: ({ sceneId }) => ({
                    url: `/api/studio/change_scene/${sceneId}`,
                    method: 'POST',
                }),
            }),
            changeOrientationStudio: builder.mutation<UserStudio, { orientation: UserStudio["orientation"] }>({
                query: ({ orientation }) => ({
                    url: `/api/studio/`,
                    method: 'PUT',
                    body: {
                        orientation
                    }
                }),
                invalidatesTags: [STUDIO]
            }),
            changeLogoStudio: builder.mutation<void, { id: Logo["id"]; logo: Logo}>({
                query: ({ id, logo }) => ({
                    url: `/api/studio/scenes/change_logo/${id}`,
                    method: 'POST',
                    body: logo
                }),
                invalidatesTags: [STUDIO]
            }),
            updateQualityRoom: builder.mutation<UserStudio, { resolution: UserStudio["resolution"] }>({
                query: ({ resolution }) => ({
                    url: `/api/studio/`,
                    method: 'PUT',
                    body: { resolution }
                }),
                invalidatesTags: [STUDIO]
            }),
        };
    }
});

export const {
    useGetBroadcastsQuery,
    useGetTranscodePresetsQuery,
    useCreateTranscodePresetMutation,
    useDeleteTranscodePresetMutation,
    useUpdateTranscodePresetMutation,
    useGetUserQuery,
    useDeleteBroadcastMutation,
    useUpdateBroadcastMutation,
    useCreateBroadcastMutation,
    useGetBroadcastQuery,
    useCreateChannelMutation,
    useCreateRutubeChannelMutation,
    useGetChannelCategoriesQuery,
    useGetChannelsQuery,
    useRefreshChannelMutation,
    useLazyConnectTelegramQuery,
    useFetchRestreamTemplatesWithBroadcastQuery,
    useUpdateChannelMutation,
    useDeleteChannelMutation,
    useDeleteTelegramConnectMutation,
    useGetDefaultTemplatesQuery,
    useDeleteChannelsMutation,
    useEnableChannelMutation,
    useDisableChannelMutation,
    useToggleChannelsMutation,
    useStartBroadcastMutation,
    useStopBroadcastMutation,
    useChangeRtmpKeyMutation,
    usePlannedBroadcastMutation,
    useLazyGetVkPlannedBroadcastQuery,
    useGetTariffsQuery,
    useBuyTariffMutation,
    useGetTransactionsQuery,
    useSignInMutation,
    useRegisterMutation,
    useLogoutMutation,
    useChangeEmailMutation,
    useChangePasswordMutation,
    usePasswordRecoveryMutation,
    usePasswordResetMutation,
    useUploadAvatarMutation,
    useWidgetSettingsMutation,
    useGetChatMessagesQuery,
    useLazyGetUserQuery,
    useLazyGetBroadcastQuery,
    useUpdateRefTokenMutation,
    useGetNotificationsQuery,
    useLazyGetNotificationsQuery,
    useToggleMailingMutation,
    useUpdateUtmMutation,
    useLazyGetChannelStatusQuery,
    useGetChannelKickLinkQuery,
    useMarkNotificationAsReadMutation,
    useSelectYoutubeStreamsMutation,
    useSelectVkStreamsMutation,
    useGetStudioQuery,
    useLazyGetStudioQuery,
    useCreateSceneMutation,
    useDeleteSceneMutation,
    useStartStudioBroadcastMutation,
    useStopStudioBroadcastMutation,
    useUpdateSceneMutation,
    useChangeTemplateStudioMutation,
    useChangeBackgroundStudioMutation,
    useChangeSceneStudioMutation,
    useChangeOrientationStudioMutation,
    useChangeLogoStudioMutation,
    useUpdateQualityRoomMutation,
    useCreateTelegramChatLinkMutation,
    useUpdateTelegramChatModeMutation,
    useGetChannelVkVideoLiveLinkQuery
} = apiSlice;